import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./HorizontalAgePicker.css";

const ITEM_WIDTH = 56;
const MIN_AGE = 18;
const MAX_AGE = 99;

function clampAge(value) {
  return Math.max(MIN_AGE, Math.min(MAX_AGE, value));
}

export function buildAgeOptions(min = MIN_AGE, max = MAX_AGE) {
  return Array.from({ length: max - min + 1 }, (_, index) => min + index);
}

export default function HorizontalAgePicker({
  value,
  onChange,
  min = MIN_AGE,
  max = MAX_AGE,
}) {
  const scrollRef = useRef(null);
  const scrollEndTimer = useRef(null);
  const isUserScrollingRef = useRef(false);
  const options = useMemo(() => buildAgeOptions(min, max), [min, max]);
  const [focusedIndex, setFocusedIndex] = useState(0);

  const resolvedValue = value === "" || value == null ? null : clampAge(Number(value));

  const scrollToIndex = useCallback((index, behavior = "smooth") => {
    const list = scrollRef.current;
    if (!list || index < 0 || index >= options.length) return;
    list.scrollTo({ left: index * ITEM_WIDTH, behavior });
  }, [options.length]);

  const updateFocusedFromScroll = useCallback(() => {
    const list = scrollRef.current;
    if (!list || options.length === 0) return;
    const index = Math.round(list.scrollLeft / ITEM_WIDTH);
    setFocusedIndex(Math.max(0, Math.min(options.length - 1, index)));
  }, [options.length]);

  useEffect(() => {
    if (resolvedValue == null) return;
    const index = options.indexOf(resolvedValue);
    if (index < 0) return;
    setFocusedIndex(index);
    if (isUserScrollingRef.current) return;
    window.requestAnimationFrame(() => {
      scrollToIndex(index, "auto");
    });
  }, [resolvedValue, options, scrollToIndex]);

  const syncFromScroll = useCallback(() => {
    const list = scrollRef.current;
    if (!list || options.length === 0) return;
    const index = Math.round(list.scrollLeft / ITEM_WIDTH);
    const nextAge = options[Math.max(0, Math.min(options.length - 1, index))];
    if (nextAge !== resolvedValue) {
      onChange?.(String(nextAge));
    }
  }, [onChange, options, resolvedValue]);

  const handleScroll = () => {
    isUserScrollingRef.current = true;
    updateFocusedFromScroll();
    window.clearTimeout(scrollEndTimer.current);
    scrollEndTimer.current = window.setTimeout(() => {
      const list = scrollRef.current;
      if (!list) return;
      const index = Math.round(list.scrollLeft / ITEM_WIDTH);
      const clamped = Math.max(0, Math.min(options.length - 1, index));
      list.scrollTo({ left: clamped * ITEM_WIDTH, behavior: "smooth" });
      syncFromScroll();
      isUserScrollingRef.current = false;
    }, 80);
  };

  const paddingX = `calc(50% - ${ITEM_WIDTH / 2}px)`;

  return (
    <div className="horizontal-age-picker">
      <div className="horizontal-age-picker__frame">
        <div className="horizontal-age-picker__highlight" aria-hidden />
        <div
          ref={scrollRef}
          className="horizontal-age-picker__track"
          style={{ paddingLeft: paddingX, paddingRight: paddingX }}
          onScroll={handleScroll}
        >
          {options.map((age, index) => (
            <div
              key={age}
              role="button"
              tabIndex={0}
              className={`horizontal-age-picker__item${focusedIndex === index ? " horizontal-age-picker__item--active" : ""}`}
              onClick={() => {
                onChange?.(String(age));
                setFocusedIndex(index);
                scrollToIndex(index);
              }}
              onKeyDown={(event) => {
                if (event.key !== "Enter" && event.key !== " ") return;
                event.preventDefault();
                onChange?.(String(age));
                setFocusedIndex(index);
                scrollToIndex(index);
              }}
            >
              {age}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
